import { createSlice } from '@reduxjs/toolkit';

import type { License } from '@webapp/account/src/resources/profile/license';
import { ConfirmType } from '@webapp/account/src/resources/profile/profile';
import type { Profile } from '@webapp/account/src/resources/profile/profile';
import { AccountRoutes } from '@webapp/account/src/resources/routes';

import { Alert } from '@webapp/common/components/message';

import {
    authorize,
    changeEmailUnconfirmed,
    changePassword,
    confirmDevice,
    confirmDeviceHashCode,
    confirmEmail,
    confirmResetPassword,
    getProfile,
    getQuotas,
    register,
    resendConfirmEmail,
    resetPassword,
    saveProfile,
} from './async';
import type { ProfileState } from './state';
import { initialState } from './state';

const fetch = (state: ProfileState) => {
    state.fetch = true;
    state.fetchErrorMessage = null;
    state.fetchError = null;
    state.fetchSuccess = null;
    state.profileLoading = true;
};

const fetchSuccess = (
    state: ProfileState,
    { payload: { profile, license } }: { payload: { profile: Profile; license: License } },
) => {
    state.profile = profile;
    state.license = license;
    state.authSuccess = profile.deviceConfirmed;
    state.fetch = false;
    state.profileLoading = false;

    Alert.destroy();
};

const fetchError = (state: ProfileState, { payload, type }: { payload?: any; type: string }) => {
    // payload может прийти без message (rejectWithValue без значения) — не роняем редьюсер (FRONT-2ABQ).
    state.fetch = false;
    state.fetchError = type.replace('/rejected', '');
    state.fetchErrorMessage = payload?.message ?? null;
};

const success = (state: ProfileState, { type }: { type: string }) => {
    state.fetch = false;
    state.fetchSuccess = type.replace('/fulfilled', '');
};

export const profileSlice = createSlice({
    name: 'profile',
    initialState,
    reducers: {
        logout: (s: ProfileState) => {
            // eslint-disable-next-line no-unused-vars
            s = initialState;
        },
        incUserLicence: (s: ProfileState) => {
            s.license.userCount -= 1;
        },
        licenseApply: (s: ProfileState) => {
            s.license.oneCountUsed += 1;
        },
    },
    extraReducers: (builder) =>
        builder
            // authorize
            .addCase(authorize.pending, fetch)
            .addCase(authorize.fulfilled, fetchSuccess)
            .addCase(authorize.rejected, fetchError)
            // getProfile
            .addCase(getProfile.pending, (state, { meta: { arg } }) => {
                if (!arg?.auth) {
                    state.profileLoading = true;
                }
            })
            .addCase(getProfile.fulfilled, fetchSuccess)
            .addCase(getProfile.rejected, (state) => {
                state.fetch = false;
                state.profileLoading = false;
            })
            // getQuotas
            .addCase(getQuotas.fulfilled, (state, { payload: { license } }) => {
                state.license = license;
                state.profileLoading = false;
            })
            // confirmDevice
            .addCase(confirmDevice.fulfilled, (state, { payload: { confirm_type } }) => {
                state.profile.confirmType = confirm_type;
            })
            // confirmDeviceHashCode
            .addCase(confirmDeviceHashCode.fulfilled, (state) => {
                state.profile.confirmDeviceMessage = null;
                state.profile.confirmDeviceResult = true;

                if (state.profile.confirmType === ConfirmType.CODE) {
                    AccountRoutes.account.assign();
                }
            })
            .addCase(confirmDeviceHashCode.rejected, (state, { payload }) => {
                const { message } = payload as { message: string };

                state.profile.confirmDeviceMessage = message;
                state.profile.confirmDeviceResult = false;
            })
            // register
            .addCase(register.pending, fetch)
            .addCase(register.fulfilled, success)
            .addCase(register.rejected, fetchError)
            // confirmEmail
            .addCase(confirmEmail.pending, fetch)
            .addCase(confirmEmail.fulfilled, success)
            .addCase(confirmEmail.rejected, fetchError)
            // resetPassword
            .addCase(resetPassword.pending, fetch)
            .addCase(resetPassword.fulfilled, (state) => {
                state.fetch = false;
            })
            .addCase(resetPassword.rejected, fetchError)
            // saveProfile
            .addCase(saveProfile.fulfilled, (state, { payload: { profile } }) => {
                Object.assign(state.profile, profile);
            })
            .addCase(saveProfile.rejected, (state) => {
                state.fetch = false;
            })
            // confirmResetPassword
            .addCase(confirmResetPassword.pending, fetch)
            .addCase(confirmResetPassword.fulfilled, success)
            .addCase(confirmResetPassword.rejected, fetchError)
            // changePassword
            .addCase(changePassword.pending, fetch)
            .addCase(changePassword.fulfilled, (state, action) => {
                success(state, action);
                state.profileLoading = false;
            })
            .addCase(changePassword.rejected, fetchError)
            // resendConfirmEmail
            .addCase(resendConfirmEmail.fulfilled, success)
            .addCase(resendConfirmEmail.rejected, fetchError)
            // changeEmailUnconfirmed
            .addCase(changeEmailUnconfirmed.fulfilled, success)
            .addCase(changeEmailUnconfirmed.rejected, fetchError),
});
